<template>

  <i-page-shell :side-nav-items="[
     {
      label: 'RHC Program Tools',
      heading: true
    },
    {
      label: 'Rurality Tier Search Tool',
      href: '#/'
    },
    {
      label: 'Rates Database',
      href: '#/ratesSearch',
      active: true
    }
  ]" header-title="Rural Health Care Program Tools">
    <template v-slot:app-footer>
      <i-app-footer :legal-links="[ {
          text: 'Version ' + appversion
        }]"/>
    </template>
    <i-grid>
      <i-form id="ratesSearchForm" @submit="submitForm">
        <i-long-form-text id="page-info">
          <h1 id="page-title">Rates Database</h1>
          <p id="ratesdb-waiver-notice" style="color: RED;">
            Note: on April 8, 2021, the FCC released <span>
                   <a id="fcc-order-link" target="_blank" :href="fccOrderLink" aria-labelledby="ratesdb-waiver-notice">Order DA 21-394</a>
                 </span>, waiving the Rates Database requirement for FY2021 and FY2022. <span>
                   <a id="learn-more-link" target="_blank" :href="learnMoreLink" aria-labelledby="learn-more-link">Learn more</a>
                 </span> about how to submit your FY2021 and FY2022 funding request (FCC Form 466).
          </p>
          <p id="help-text">
            Populate each of the fields below and then click Calculate to see the median rate. The available
            drop-down options for Service Type, which is an optional field, depend on the Category of
            Service selected - Data or Voice. If you are looking for voice rates, input 0 for Bandwidth.
            For more information about median rates, terms used, and using this tool, visit the
            <span>
                            <a id="help-link" target="_blank" :href="ratesToolHelpLink"
                               aria-labelledby="help-text">
                             Rates Database homepage.
                        </a>
                        </span>
          </p>
        </i-long-form-text>


        <i-notification id="error-nomatch" variant="error" v-if="nomatch"
                        message="There are no available rates for the service parameters and geographic area selected for calculating a median rate. Per the 2019 RHC Reform Order, 34 FCC Rcd 7335, para. 61, use available rates from the next lowest rurality tier (e.g., if no rates for Extremely Rural tier, use Rural tier rates). If you continue to receive this error and/or have questions, then please email RHC-Assist@usac.org."/>
        <i-notification id="error-servicedown" variant="error" v-if="serviceDown"
                        message="Search tool is currently unavailable. We apologize for the inconvenience, please try again later."/>

        <i-fieldset>
          <i-grid>
            <i-input-group id="state" label="Location" :invalid="state.invalid"
                           :notification-text="state.errorMessage"
                           class="indi-col-xs-3">
              <i-select id="state-input" :required="false" v-model="state.value"
                        :invalid="state.invalid" aria-labelledby="state"
                        :options="allStates"/>
            </i-input-group>

            <i-grid class="indi-col-xs-5">
              <i-input-group id="ruralTier" label="Rate Tier" :invalid="ruralTier.invalid"
                             :notification-text="ruralTier.errorMessage" class="indi-col-xs-11">
                <i-select id="ruralTier-input" :required="false" v-model="ruralTier.value"
                          aria-labelledby="ruralTier"
                          :invalid="ruralTier.invalid"
                          :options="allRuralTiers"/>
              </i-input-group>
              <i-grid class="indi-col-xs-1">
                <div class="indi-col-xs-offset-6 vertical-line" id="divider"
                     style="width: 3px;height:150px;background-color: #e0e0e0;"/>
              </i-grid>
            </i-grid>
            <i-input-group class="indi-col-xs-4" id="category-of-service" :key="categoryOfServiceKey"
                           label="Category of Service"
                           :invalid="categoryOfService.invalid"
                           :notification-text="categoryOfService.errorMessage"
            >
              <i-select id="category-of-service-input" :required="false"
                         @input="updateServiceTypeList"
                        aria-labelledby="category-of-service"
                        v-model="categoryOfService.value"
                        :invalid="categoryOfService.invalid" :options="categoryOfServiceList"/>
            </i-input-group>
          </i-grid>
          <i-grid>
            <i-input-group id="bandwidth" class="indi-col-xs-3"
                           :invalid="bandwidth.invalid"
                           :notification-text="bandwidth.errorMessage">

              <i-label id="bandwidth-label" text="Bandwidth(MB)" for="bandwidth-input">
                <i-tooltip id="bandwidth-tooltip" slot="annotation"
                            :text="bandwidthhelp"/>
              </i-label>
              <i-input id="bandwidth-input"  aria-labelledby="bandwidth-label"
                       @focus="clearBandwidth()" @blur="onblurBandwidth()" :maxLength="20"
                       type="text" :invalid="bandwidth.invalid"
                       v-model="bandwidth.value">
              </i-input>
              <i-help-text id="bandwidth-format" text="xxxx.xxx"/>
            </i-input-group>

            <i-grid class="indi-col-xs-5">
              <i-input-group class="indi-col-xs-11" id="best-or-dedicated"
                             :invalid="bestOrDedicated.invalid"
                             :notification-text="bestOrDedicated.errorMessage"
                             label="Best Efforts/Dedicated Service">
                <i-select id="best-or-dedicated-input" :required="false"
                          v-model="bestOrDedicated.value" aria-labelledby="best-or-dedicated"
                          :invalid="bestOrDedicated.invalid" :options="[{
                               label: 'Select',
                               value: ''
                            },
                                {
                                    label: 'Best Efforts',
                                    value: 'Best Efforts'
                                },{
                                    label: 'Dedicated Service',
                                    value: 'Dedicated Service'
                                }
                            ]"/>
              </i-input-group>
              <i-grid class="indi-col-xs-1">
                <div class="indi-col-xs-offset-6 vertical-line" id="divider_second_line"
                     style="width: 3px;height:140px;background-color: #e0e0e0;"/>
              </i-grid>
            </i-grid>

            <i-input-group class="indi-col-xs-4" id="service-type" :key="serviceTypeKey">
              <i-label text="Service Type" input-for="service-type-input">
                                        <span slot="annotation"
                                              class="indi-form__label-optional-indicator" style="color:#717171">(Optional)</span>
              </i-label>
              <i-select id="service-type-input" :required="false"
                        @input="updateCategoryOfServiceList"
                        aria-labelledby="service-type" v-model="serviceType.value"
                        :options="serviceTypeList"/>
            </i-input-group>
          </i-grid>
        </i-fieldset>

        <i-grid>
          <i-input-group class="indi-col-xs-6">
            <i-button id="clearall-btn"  label="Clear All" @click="clearAll"
                      variant="secondary"/>
          </i-input-group>
          <i-input-group class="indi-col-xs-6 indi-form__input-group--text-align-right">
            <i-button id="calculate-btn" label="Calculate" type="button"
                      @click="validateForm()"
                      variant="primary"/>
          </i-input-group>
        </i-grid>
        <br/>
        <div id="rates-results" v-if="calculateComplete && !nomatch && !serviceDown">
          <i-long-form-text>
            <h3 id="calculation-title">Your {{ ruralUrban }} Median Rate Calculation:</h3>
            <br/>
            <div id="median-rate" style="border: solid 1px #ffab00; width: 536px;">
              <i-grid>
                              <span aria-labelledby="calculation-title">
                                <div id="median-rate-label" class="indi-col-xs-6">
                                    <p style="text-align: right;">{{ ruralUrban }} median rate:</p>
                                </div>
                                <div id="median-rate-result" class="indi-col-xs-3" aria-labelledby="median-rate-label">
                                    <p><strong>${{ medianRate }}</strong></p>
                                </div>
                               </span>
              </i-grid>
            </div>
          </i-long-form-text>

          <br/>


          <i-grid>
            <i-long-form-text class="indi-col-xs-12" style="margin-bottom: 32px;">
              <h3>Median Rate Calculation Data</h3>
            </i-long-form-text>
            <i-pagination class="indi-col-xs-12" id="rates-pagination" aria-labelledby="download-btn"
                          :results-current="records.body.length"
                          :results-total="totalRowCount"
                          resultsLabelSuffix="records"
                          :select-options="[{label: 25, value: 25},  {label: 5, value: 5},
                                            {label: 10, value: 10},  {label: 50, value: 50}]"
                          :pagination-current="currentPage"
                          :pagination-total="totalPageCount"
                          @onSelectInput="changeRowsPerPage"
                          @onNavChange="changePage">
              <i-button slot="actions" id="download-btn" label="Download Data"
                        size="small"
                        variant="tertiary" @click="downloadCSV">
                <i-icon size="xsmall" valign="valign-middle">
                  <IconDownload/>
                </i-icon>
              </i-button>
              <i-data-table id="rate-data-table" :table-data="records"/>
            </i-pagination>
          </i-grid>
          <i-long-form-text>
            <p>*The data download may include duplicate rates. Duplicate rates are considered as a
              single rate for purposes of calculating the median available rate. For addtional
              information, see the <a target="_blank" :href="ratesToolHelpLink">Rates Database home
                page.</a>
            </p>
          </i-long-form-text>
        </div>
        <i-loader id="loader" v-if="loading" variant="overlay"/>
      </i-form>
    </i-grid>
  </i-page-shell>
</template>
<style>
/* Firefox remove number arrow*/
input[type=number] {
  -moz-appearance: textfield;
}
</style>
<script>
import axios from "axios";
import {median} from "mathjs";

import {
  IAppFooter,
  IButton,
  IDataTable,
  IFieldset,
  IForm,
  IGrid,
  IHelpText,
  IIcon,
  IInput,
  IInputGroup,
  ILabel,
  ILoader,
  ILongFormText,
  INotification,
  IPageShell,
  IPagination,
  ISelect,
  ITooltip
} from 'indigo-component-library';

import {dataService, setFocusOnError} from "../assets/data-service";

import {IconDownload} from 'indigo-component-library/src/components/Icon/Icons';

const FileDownload = require('js-file-download');
export default {
  props: {
    selected_ruralityTier: {
      type: String,
      required: false
    },
    selected_state: {
      type: String,
      required: false
    }
  },
  components: {
    IGrid,
    IForm,
    IButton,
    IFieldset,
    IInputGroup,
    IInput,
    ISelect,
    ITooltip,
    ILabel,
    IDataTable,
    IPagination,
    ILongFormText,
    IIcon,
    IconDownload,
    INotification,
    ILoader,
    IHelpText,
    IPageShell,
    IAppFooter
  },
  data() {
    return {
      appversion: this.$store.getters.appVersion,
      serviceTypeKey: 1000,
      categoryOfServiceKey: 0,
      ratesToolHelpLink: '',
      fccOrderLink: '',
      learnMoreLink: '',
      bandwidthhelp: 'Data used for median rate calculation will automatically include 30%+/- range of requested bandwidth.',
      currentRowsPerPage: 25,
      currentPage: 1,
      totalRowCount: 0,
      state: {
        value: this.selected_state == undefined ? '' : this.selected_state,
        invalid: false,
        errorMessage: ''
      },
      medianRate: '0.00',
      ruralTier: {
        value: this.selected_ruralityTier == undefined ? '' : this.selected_ruralityTier,
        invalid: false,
        errorMessage: ''
      },
      categoryOfService: {
        value: '',
        invalid: false,
        errorMessage: ''
      },
      serviceType: {},
      bandwidth: {
        value: 'Enter bandwidth',
        invalid: false,
        errorMessage: ''
      },
      entries: [],
      bestOrDedicated: {
        value: '',
        invalid: false,
        errorMessage: ''
      },
      allStates: [],
      allRuralTiers: [],
      serviceTypeList: [],
      categoryOfServiceList: [],
      calculateComplete: false,
      nomatch: false,
      loading: false,
      serviceDown: false,
      ruralTierList: [],
      ruralUrban: ''
    }
  },
  computed: {
    records() {
      const tableData = {
        head: [
          {
            label: 'State',
            static: true
          },
          {
            label: 'Rate Tier',
            static: true
          },
          {
            label: 'Category of Service',
            static: true
          },
          {
            label: 'Service Type',
            static: true
          },
          {
            label: 'Bandwidth (MB)',
            static: true
          },
          {
            label: 'Best Efforts/ Dedicated Service',
            static: true
          },
          {
            label: 'Rate($)*',
            static: true
          },
          {
            label: 'Source of Data',
            static: true
          }
        ],
        body: dataService(this.entries, this.currentRowsPerPage, this.currentPage)
      }
      return tableData;
    },
    totalPageCount() {
      const pageCount = Math.ceil(this.totalRowCount / this.currentRowsPerPage);
      return pageCount;
    }
  },
  methods: {
    clearBandwidth() {
      if (this.bandwidth.value.trim() == 'Enter bandwidth') {
        this.bandwidth.value = '';
      }
    },
    onblurBandwidth() {
      if (this.bandwidth.value == '') {
        this.resetBandwidth();
      }
    },
    resetBandwidth() {
      this.bandwidth.value = 'Enter bandwidth';
    },

    resetCalculation() {
      this.calculateComplete = false;
    },
    clearAll() {
      this.state.value = "";
      this.ruralTier.value = "";
      this.categoryOfService.value = "";
      this.serviceType.value = "";
      this.bandwidth.value = 'Enter bandwidth';
      this.bestOrDedicated.value = "";
      this.state.invalid = false;
      this.ruralTier.invalid = false;
      this.serviceType.invalid = false;
      this.categoryOfService.invalid = false;
      this.bandwidth.invalid = false;
      this.bestOrDedicated.invalid = false;
      this.pullAllCategoryOfServices();
      this.pullAllServiceTypes();
      this.calculateComplete = false;
      this.nomatch = false;
      this.serviceDown = false;


    },
    validateForm() {
      if (this.state.value.length === 0) {
        this.state.invalid = true;
        this.state.errorMessage = 'State required';
      } else {
        this.state.invalid = false;
        this.state.errorMessage = '';
      }

      if (this.ruralTier.value.length === 0 || !this.ruralTierList.includes(this.ruralTier.value)) {
        this.ruralTier.invalid = true;
        this.ruralTier.errorMessage = 'Tier required';
      } else {
        this.ruralTier.invalid = false;
        this.ruralTier.errorMessage = '';
      }
      if (this.categoryOfService.value.length === 0) {
        this.categoryOfService.invalid = true;
        this.categoryOfService.errorMessage = 'Category required';
      } else {
        this.categoryOfService.invalid = false;
        this.categoryOfService.errorMessage = '';
      }

      if (this.categoryOfService.value != 'Voice') {
        if (this.bandwidth.value.trim().length === 0 || this.bandwidth.value === 'Enter bandwidth') {
          this.bandwidth.invalid = true;
          this.bandwidth.errorMessage = 'Bandwidth required if Category of Service is Data';
        } else if (!validateBandwidth(this.bandwidth.value.trim())) {
          this.bandwidth.invalid = true;
          this.bandwidth.errorMessage = 'Fields may only contain numbers and cannot exceed three decimals';
        } else {
          this.bandwidth.invalid = false;
          this.bandwidth.errorMessage = '';
        }
      } else {
        this.bandwidth.invalid = false;
        this.bandwidth.errorMessage = '';
      }
      if (this.bestOrDedicated.value.length === 0) {
        this.bestOrDedicated.invalid = true;
        this.bestOrDedicated.errorMessage = 'Best Efforts or Dedicated Service required';
      } else {
        this.bestOrDedicated.invalid = false;
        this.bestOrDedicated.errorMessage = '';
      }
      if (!this.state.invalid && !this.ruralTier.invalid && !this.categoryOfService.invalid && !this.bandwidth.invalid && !this.bestOrDedicated.invalid) {
        this.calculate();
      } else {
        let fields = new Map();
        fields.set("state-input", this.state.invalid);
        fields.set("ruralTier-input", this.ruralTier.invalid);
        fields.set("category-of-service-input", this.categoryOfService.invalid);
        fields.set("bandwidth-input", this.bandwidth.invalid);
        fields.set("best-or-dedicated-input", this.bestOrDedicated.invalid);

        // for (let[id, invalid] of fields) {
        //     console.log("validateForm. (id ="+ id +";  invalid = " + invalid);
        //   }

        setFocusOnError(fields);

      }
    },
    submitForm(e) {
      e.preventDefault();
    },
    calculate() {
      this.loading = true;
      axios.get('/ratesdb/distinctRatesData', {
        params: {
          state: this.state.value,
          ruralTier: this.ruralTier.value,
          categoryOfService: this.categoryOfService.value,
          bestOrDedicated: this.bestOrDedicated.value,
          bandwidth: this.bandwidth.value
        }
      }).then(response => {
        this.currentPage = 1;
        this.serviceDown = false;
        this.ruralUrban = (this.ruralTier.value === 'Urban' ? 'Urban' : 'Rural');
        if (response.data != undefined && response.data.length > 0) {
          this.entries = response.data;
          let rateArray = [];
          for (let i = 0; i < this.entries.length; i++) {
            if (!isNaN(this.entries[i].rate))
              rateArray.push(Number(this.entries[i].rate));
          }
          if (rateArray.length > 0)
            this.medianRate = median(rateArray).toFixed(2);
          this.totalRowCount = response.data.length;
          this.nomatch = false;
          setTimeout(function () {
            let btn = document.getElementById("download-btn");
            if (btn != undefined) {
              btn.querySelector('.indi-button__icon-text').style.padding = "0px";
            }
            let datatable = document.getElementById("rate-data-table");
            if (datatable != undefined) {
              datatable.querySelector(".indi-data-table__table").setAttribute("title", "rate-data");
            }
          }, 1000);

        } else {
          this.nomatch = true;
        }
        this.calculateComplete = true;
        this.loading = false;
      }).catch(error => {
        console.log(error);
        this.calculateComplete = true;
        this.nomatch = false;
        this.serviceDown = true;
        this.loading = false;
      });

    },
    changeRowsPerPage(rowsPerPage) {
      this.currentRowsPerPage = rowsPerPage > this.totalRowCount ? this.totalRowCount : rowsPerPage;
      this.currentPage = 1; // Reset to first page when the number of rows per page changes
    },
    changePage(page) {
      this.currentPage = page;
    },
    downloadCSV() {
      axios.get('/ratesdb/generateCSV', {
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
        params: {
          state: this.state.value,
          ruralTier: this.ruralTier.value,
          categoryOfService: this.categoryOfService.value,
          bestOrDedicated: this.bestOrDedicated.value,
          bandwidth: this.bandwidth.value
        }
      }).then(response => {
        this.serviceDown = false;
        FileDownload(response.data, "Rates_DB_FY" + this.fundingYear + "_V" + this.version + ".csv");
      }).catch(error => {
        this.serviceDown = true;
        console.log(error);
      });
    },
    updateServiceTypeList() {
      if (this.categoryOfService.value != '') {
        axios.get('/ratesdb/serviceTypesByCategory', {
          params: {
            categoryOfService: this.categoryOfService.value
          }
        }).then(response => {
          if (response.data != undefined && response.data.length > 0) {
            this.serviceTypeList = [{label: "Select type", value: ''}];
            response.data.forEach(st => this.serviceTypeList.push({
              label: st,
              value: st
            }));
          }
          this.serviceTypeKey++;
        });
      }

    },
    updateCategoryOfServiceList() {
      if (this.serviceType.value != '') {
        axios.get('/ratesdb/categoryOfServicesByServiceType', {
          params: {
            serviceType: this.serviceType.value
          }
        }).then(response => {
          if (response.data != undefined && response.data.length > 0) {
            this.categoryOfServiceList = [{label: "Select category", value: ''}];
            response.data.forEach(st => this.categoryOfServiceList.push({
              label: st,
              value: st
            }));
          }
          this.categoryOfServiceKey++;
        });
      }
    },
    pullAllStates() {
      axios.get('/ratesdb/states').then(response => {
        this.allStates.push({
          label: 'Select a state',
          value: ''
        });
        response.data.forEach(st => this.allStates.push({
          label: st,
          value: st
        }));
      });
    },
    pullSystemProperties() {
      axios.get('/ratesdb/systemProperties').then(response => {
        this.serviceDown = false;
        let data = response.data;
        if (data != undefined && data.length > 0) {
          for (let i = 0; i < data.length; i++) {
            switch (data[i].name) {
              case 'fundingYear':
                this.fundingYear = data[i].value;
                break;
              case 'version':
                this.version = data[i].value;
                break;
              case 'ratesToolHelpLink':
                this.ratesToolHelpLink = data[i].value;
                break;
              case 'fccOrderLink':
                this.fccOrderLink = data[i].value;
                break;
              case 'learnMoreLink':
                this.learnMoreLink = data[i].value;
                break;
            }
          }
        }
      }).catch(error => {
        this.serviceDown = true;
        console.log(error);
      });
    },
    pullAllServiceTypes() {
      this.serviceTypeList = [{
        label: 'Select type',
        value: ''
      }];
      axios.get('/ratesdb/allServiceTypes').then(response => {
        response.data.forEach(st => this.serviceTypeList.push({
          label: st,
          value: st
        }));
      });
    },
    pullAllRuralTiers() {
      axios.get('/ratesdb/ruralTiers').then(response => {
        this.allRuralTiers.push({
          label: 'Select tier',
          value: ''
        });
        response.data.forEach(st => {
          this.allRuralTiers.push({
            label: st,
            value: st
          });
          this.ruralTierList.push(st);
        });
      });
    },
    pullAllCategoryOfServices() {
      this.categoryOfServiceList = [{
        label: 'Select category',
        value: ''
      }];
      axios.get('/ratesdb/categoryOfServices').then(response => {
        response.data.forEach(st => this.categoryOfServiceList.push({
          label: st,
          value: st
        }));
      });
    }
  },
  mounted() {
    this.pullAllStates();
    this.pullAllRuralTiers();
    this.pullAllCategoryOfServices();
    this.pullAllServiceTypes();
    this.pullSystemProperties();
  }
}

function validateBandwidth(elementValue) {
  let bandWidthPattern = /^\d+(\.\d{1,3})?$/;
  return bandWidthPattern.test(elementValue);
}
</script>